import { useMemo } from 'react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ThumbsDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { remarkDashRule } from '@arthelper/shared/chat-markdown';

import { markdownComponents } from '../Chat/tool-renderers/common';
import ToolCallWrapper from './ToolCallWrapper';

// Assistant text only. This viewer is an audit surface, so what the user actually typed —
// including a literal setext heading — has to render as they sent it.
const USER_REMARK_PLUGINS = [remarkGfm];
const ASSISTANT_REMARK_PLUGINS = [remarkGfm, remarkDashRule];

interface ChatPart {
  id: string;
  role: string;
  partIndex: number;
  type: string;
  content?: string;
  toolCall?: any;
  attachment?: any;
  reasoning?: string;
  usage?: any;
  metadata?: any;
  good?: boolean | null;
  notes?: string;
  createdAt: string;
}

interface ChatDisplayProps {
  chatParts: ChatPart[];
  className?: string;
}

function MessageFeedbackIndicator({ good, notes }: { good?: boolean | null; notes?: string }) {
  const { t } = useTranslation('chat');

  if (good === false) {
    return (
      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
        <div className="flex items-center gap-2 text-red-700 text-sm font-medium mb-1">
          <ThumbsDown className="w-4 h-4" />
          <span>{t('display.negative-feedback')}</span>
        </div>
        {notes && (
          <div className="text-red-600 text-sm">
            <strong>{t('display.notes')}</strong> {notes}
          </div>
        )}
      </div>
    );
  }

  if (good === true) {
    return (
      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
        <div className="flex items-center gap-2 text-green-700 text-sm font-medium mb-1">
          <span>✓ {t('display.positive-feedback')}</span>
        </div>
        {notes && (
          <div className="text-green-600 text-sm">
            <strong>{t('display.notes')}</strong> {notes}
          </div>
        )}
      </div>
    );
  }

  return null;
}

function ChatDisplay({ chatParts, className = '' }: ChatDisplayProps) {
  const { t } = useTranslation('chat');
  const sortedChatParts = useMemo(() => [...chatParts].sort((a, b) => a.partIndex - b.partIndex), [chatParts]);

  return (
    <div className={`space-y-4 ${className}`}>
      {sortedChatParts.map((part) => {
        const isUser = part.role === 'user';
        const content = part.content || '';
        const remarkPlugins = isUser ? USER_REMARK_PLUGINS : ASSISTANT_REMARK_PLUGINS;

        return (
          <div key={part.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[85%]">
              {/* Timestamp for messages - small and subtle */}
              {part.type === 'text' && (
                <div className="text-xs text-gray-400 mb-1">{format(new Date(part.createdAt), 'MMM d, h:mm a')}</div>
              )}

              {part.type === 'text' && (
                <div
                  className={`prose prose-sm max-w-none ${
                    isUser
                      ? 'bg-gray-100 text-gray-800 font-light text-base leading-7 py-2 px-6 ml-auto'
                      : 'bg-white text-gray-800 border border-gray-200 p-4'
                  }`}
                >
                  <ReactMarkdown components={markdownComponents} remarkPlugins={remarkPlugins}>
                    {content}
                  </ReactMarkdown>
                </div>
              )}

              {part.type === 'tool_invocation' && part.toolCall && <ToolCallWrapper toolCall={part.toolCall} />}

              {part.type === 'attachment' && part.attachment && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">{t('display.attachment')}</div>
                  <div className="bg-gray-50 p-2 text-sm">
                    {typeof part.attachment === 'object' ? JSON.stringify(part.attachment) : part.attachment}
                  </div>
                </div>
              )}

              {part.type === 'reasoning' && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">{t('display.reasoning')}</div>
                  <div className="bg-gray-50 p-2 text-sm">{part.reasoning}</div>
                </div>
              )}

              {part.type === 'usage' && part.usage && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500">{t('display.usage')}</div>
                  <div className="text-xs text-gray-700 font-mono break-all whitespace-normal">
                    {typeof part.usage === 'object' ? JSON.stringify(part.usage, null, 0) : part.usage}
                  </div>
                </div>
              )}

              {/* Show feedback indicator for all message types */}
              <MessageFeedbackIndicator good={part.good} notes={part.notes} />
            </div>
          </div>
        );
      })}

      {sortedChatParts.length === 0 && (
        <div className="text-center py-12 text-gray-500">{t('display.no-messages')}</div>
      )}
    </div>
  );
}

export default ChatDisplay;
